import { useParams, Link, Navigate } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sun, Zap, TrendingUp, BadgeCheck, ArrowLeft } from "lucide-react";
import { getCity, COMPANY } from "../lib/site";
import Reveal from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import TickerMarquee from "../components/sections/Marquee";
import Trust from "../components/sections/Trust";
import Gallery from "../components/sections/Gallery";
import Testimonials from "../components/sections/Testimonials";
import FAQ from "../components/sections/FAQ";
import LeadSection from "../components/sections/LeadSection";

export default function CityLanding() {
  const { slug } = useParams();
  const city = getCity(slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  if (!city) return <Navigate to="/" replace />;

  const stats = [
    { icon: Sun, label: "Peak sun hours / day", value: `${city.sunHours} hrs` },
    { icon: Zap, label: "Systems installed", value: city.installs },
    { icon: TrendingUp, label: "Avg. yearly saving", value: `₹${city.avgSaving}` },
    { icon: BadgeCheck, label: "Warranty", value: "25 yrs" },
  ];

  return (
    <main data-testid="city-page">
      {/* Hero */}
      <section ref={ref} className="relative min-h-[92vh] overflow-hidden bg-obsidian pt-32 text-cream">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img src={city.image} alt={`Solar in ${city.name}`} className="h-[115%] w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/50" />
        </motion.div>

        <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-6 pb-16 md:px-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-cream/60 transition-colors hover:text-gold">
              <ArrowLeft className="h-4 w-4" /> Back home
            </Link>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold backdrop-blur">
              <Sun className="h-4 w-4" /> {city.state}
            </p>
            <h1 className="font-heading text-5xl font-extrabold leading-[0.92] tracking-tight sm:text-6xl lg:text-7xl">
              Go solar in<br /><span className="text-gold">{city.name}.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-cream/70">{city.blurb}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#quote" data-testid="city-hero-cta" className="rounded-full bg-gold px-8 py-4 font-semibold text-obsidian transition-transform duration-300 hover:scale-95 hover:bg-gold-hover">
                Get free {city.name} quote
              </a>
              <a href={COMPANY.phoneHref} className="rounded-full border border-white/20 px-8 py-4 font-semibold text-cream transition-colors hover:bg-white/10">
                Call {COMPANY.phone}
              </a>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-[2rem] border border-white/10 bg-obsidian/70 p-7 backdrop-blur-xl md:p-8">
              <h3 className="mb-5 font-heading text-xl font-bold">Get a quote for {city.name}</h3>
              <LeadForm sourcePage={`City: ${city.name}`} defaultCity={city.name} dark />
            </div>
          </Reveal>
        </div>
      </section>

      <TickerMarquee />

      {/* Local stats */}
      <section className="bg-cream py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="rounded-3xl border border-line bg-white/60 p-7">
                  <s.icon className="h-7 w-7 text-forest" />
                  <p className="mt-5 font-heading text-3xl font-extrabold text-forest md:text-4xl">{s.value}</p>
                  <p className="mt-1 text-sm text-moss">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 grid gap-8 rounded-[2rem] border border-line bg-sand p-8 md:grid-cols-[1fr_1.3fr] md:p-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold">Subsidies & schemes</p>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-forest md:text-4xl">
                What {city.name} residents can claim
              </h2>
            </div>
            <p className="self-center text-lg leading-relaxed text-moss">{city.subsidy} Synergy Solar handles the full application and net-metering process for you — start to finish.</p>
          </Reveal>
        </div>
      </section>

      <Trust />
      <Gallery />
      <Testimonials city={city.name} />
      <FAQ />
      <LeadSection sourcePage={`City: ${city.name}`} defaultCity={city.name} title={`Ready to go solar in ${city.name}?`} />
    </main>
  );
}
